import React, { FC, useEffect, useState } from 'react';

import { translate, translateDynamic } from '@utils/locale';
import { ActionButton, CustomButton, HorizontalStepper } from '@components';
import { PublishIcon } from '@assets/icons';
import { OptionsType } from '@components/action-button/types';
import { PreIPODealStatus } from '@constants/common';
import { formatDate } from '@utils/common';
import { preIPOStatusColorMapping } from '@modules/pre-ipo-deals/pre-ipo-deals-section/common';
import DefaultColorChip from '@components/default-color-chip/DefaultColorChip';

import { CreatePreIPODealHeaderProps } from '../types';
import { PRE_IPO_PUBLISH_ACTIONS } from '../constants';

const CreatePreIPODealPageHeader: FC<CreatePreIPODealHeaderProps> = ({
  isEditPage = false,
  dealName,
  status,
  isPreIPODealFormDirty = false,
  lastUpdatedAt,
  handlePublish,
  activeStep,
  handleStepClick,
  stepCompletionStatus
}) => {
  const [publishAction, setPublishAction] = useState<OptionsType[]>([]);

  const steps = [
    translate('preIpoDeals.createPreIPODeals.horizontalStepper.companyDetails'),
    translate(
      'preIpoDeals.createPreIPODeals.horizontalStepper.valuationDetails'
    ),
    translate('preIpoDeals.createPreIPODeals.horizontalStepper.dealDetails'),
    translate('preIpoDeals.createPreIPODeals.horizontalStepper.dataRoom'),
    translate(
      'preIpoDeals.createPreIPODeals.horizontalStepper.dealConfiguration'
    ),
    ...(status === ''
      ? [
          translate(
            'preIpoDeals.createPreIPODeals.horizontalStepper.transactionDetails'
          )
        ]
      : [])
  ];

  const getPreIPOHeaderName = () => {
    if (status) {
      if (dealName)
        return translateDynamic(
          'preIpoDeals.createPreIPODeals.editPreIPODealName',
          { dealName }
        );
      return translate('preIpoDeals.createPreIPODeals.editPreIPODealTitle');
    }
    return translate('preIpoDeals.createPreIPODeals.createPreIPODealTitle');
  };

  useEffect(() => {
    if (
      status &&
      PRE_IPO_PUBLISH_ACTIONS[status as keyof typeof PRE_IPO_PUBLISH_ACTIONS]
    ) {
      const actionsArray = PRE_IPO_PUBLISH_ACTIONS[
        status as keyof typeof PRE_IPO_PUBLISH_ACTIONS
      ].map(action => ({
        id: action,
        label: translateDynamic(`dealStatus.${action}`)
      }));
      setPublishAction(actionsArray);
    }
  }, [status]);
  return (
    <div className="p-5">
      <div className="flex justify-between items-center text-[34px] font-normal">
        <div className="flex items-center">
          <span className="inline-block max-w-[500px] truncate">
            {getPreIPOHeaderName()}
          </span>
          {status && (
            <div className="flex items-center ml-4">
              <DefaultColorChip
                eachEntry={status}
                colorMapping={preIPOStatusColorMapping}
                labelValue={translateDynamic(`preIPODealStatus.${status}`)}
                variant={
                  status === PreIPODealStatus.ACTIVE ||
                  status === PreIPODealStatus.EXPIRED
                    ? 'filled'
                    : 'outlined'
                }
              />
            </div>
          )}
        </div>

        <div className="flex flex-row justify-end">
          {isEditPage &&
          [
            PreIPODealStatus.ACTIVE,
            PreIPODealStatus.EXPIRED,
            PreIPODealStatus.CLOSED
          ].includes(status as PreIPODealStatus) ? (
            <ActionButton
              handleMenuItemClick={(dealStatus: PreIPODealStatus) =>
                handlePublish(dealStatus)
              }
              options={publishAction}
              label={
                isPreIPODealFormDirty
                  ? translate('publish')
                  : translate('published')
              }
              variant="contained"
              hideDivider={true}
              isDisabled={!isPreIPODealFormDirty}
            />
          ) : (
            <CustomButton
              fullWidth={false}
              variant="contained"
              label={translate('pushToInfinyteReview')}
              onClick={() => handlePublish(PreIPODealStatus.IN_REVIEW)}
              icon={<PublishIcon />}
              customClassname="hover:bg-gradient-to-r hover:from-denimBlue hover:to-denimBlue"
            />
          )}
        </div>
      </div>
      <div className="pb-6 text-sm text-grey">
        {status === PreIPODealStatus.ACTIVE && lastUpdatedAt && (
          <div>
            {translate('lastUpdatedAt', {
              time: formatDate(lastUpdatedAt as string)
            })}
          </div>
        )}
      </div>
      <div className="w-[100%]">
        <HorizontalStepper
          steps={steps}
          activeStep={activeStep}
          handleStep={handleStepClick}
          completed={stepCompletionStatus}
        />
      </div>
    </div>
  );
};
export default CreatePreIPODealPageHeader;
